import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faUser, faPlus, faTags, faStore, faHome, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';

// Firebase imports
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { collection, onSnapshot, updateDoc, deleteDoc, doc, query, getDoc, Timestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';

// Styled Components
import {
  ListingsContainer,
  DashboardHeader,
  DashboardTitle,
  DashboardMeta,
  Tabs,
  Tab,
  LoadingMessage,
  ErrorMessage,
  DashboardContent,
  TabIcon,
  Button,
} from '../../pages/registeredUsers/styles/listings.style';

// Tabs Components
import PostAd from '../../pages/registeredUsers/tabs/PostAd';
import { Listing } from '../../types/listing';
import MyListings from './listings';
import Overview from './overview';
import Profile from './profile';

// 🔹 Firebase Function URL for payment status
const PESAPAL_STATUS_FUNCTION_URL = "https://us-central1-grema-62003.cloudfunctions.net/pesapal_status";

const ListingsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [userId, setUserId] = useState<string | null>(null);
  const [loadingFirebase, setLoadingFirebase] = useState(true);
  const [firebaseError, setFirebaseError] = useState<string | null>(null);

  const [userStallNumber, setUserStallNumber] = useState<string | null>(null);
  const [stallDetails, setStallDetails] = useState<any>(null);

  const [activeTab, setActiveTab] = useState<'create' | 'my-listings' | 'overview' | 'profile'>('create');

  const [listings, setListings] = useState<Listing[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [listingsError, setListingsError] = useState<string | null>(null);

  // 🔹 New state for payment processing
  const [paymentStatusMessage, setPaymentStatusMessage] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  // Handle authentication
  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        try {
          const anonUser = await signInAnonymously(auth);
          setUserId(anonUser.user.uid);
        } catch (error) {
          console.error("Error signing in anonymously:", error);
          setFirebaseError("Failed to authenticate anonymously. Please try again.");
        }
      }
      setLoadingFirebase(false);
    });

    // Extract params from URL
    const queryParams = new URLSearchParams(location.search);
    const stallNumFromUrl = queryParams.get('stallNumber');
    const orderTrackingId = queryParams.get('order_tracking_id');

    if (stallNumFromUrl) {
      setUserStallNumber(stallNumFromUrl);
    }

    // Handle payment callback if orderTrackingId exists
    const handlePaymentCallback = async () => {
      if (orderTrackingId) {
        setProcessingPayment(true);
        setPaymentStatusMessage("Verifying your payment details...");

        try {
          const response = await fetch(PESAPAL_STATUS_FUNCTION_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_tracking_id: orderTrackingId }),
          });

          if (response.ok) {
            const result = await response.json();
            const status = result.payment.status;
            setPaymentStatusMessage(`Payment verification successful! Status: ${status}`);

            // ✅ Keep payment info in state for display
            setPaymentDetails(result.payment);
          } else {
            console.error("Error fetching payment status:", response.statusText);
            setPaymentStatusMessage("Failed to verify payment. Please try again later.");
          }
        } finally {
          setProcessingPayment(false);
          // Clean URL by removing query params
          navigate(location.pathname, { replace: true });
        }
      }
    };

    handlePaymentCallback();

    return () => unsubscribeAuth();
  }, [location.search, navigate, location.pathname]);

  // Fetch stall details
  useEffect(() => {
    const fetchStallDetails = async () => {
      if (userStallNumber) {
        try {
          const stallDocRef = doc(db, 'stalls', userStallNumber);
          const docSnap = await getDoc(stallDocRef);
          if (docSnap.exists()) {
            setStallDetails(docSnap.data());
          } else {
            console.warn("Stall document not found for stall number:", userStallNumber);
            setStallDetails(null);
          }
        } catch (error) {
          console.error("Error fetching stall details:", error);
          setStallDetails(null);
        }
      }
    };
    fetchStallDetails();
  }, [userStallNumber]);

  // Real-time listener for listings
  useEffect(() => {
    if (userStallNumber) {
      setLoadingListings(true);
      setListingsError(null);
      try {
        const listingsCollectionRef = collection(db, `stalls/${userStallNumber}/listings`);
        const q = query(listingsCollectionRef);

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const fetchedListings = snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            } as Listing;
          });
          setListings(fetchedListings);
          setLoadingListings(false);
        }, (error) => {
          console.error("Error fetching listings:", error);
          setListingsError("Failed to load listings. Please try again.");
          setLoadingListings(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error setting up listings listener:", error);
        setListingsError("Failed to set up real-time listings. Check console.");
        setLoadingListings(false);
      }
    } else if (!loadingFirebase) {
      setListingsError("Stall number not found. Please log in again.");
      setLoadingListings(false);
    }
  }, [userStallNumber, loadingFirebase]);

  // Delete listing
  const handleDeleteListing = async (listingIdToDelete: string, imageUrls: string[], videoUrls: string[]) => {
    if (!userId || !userStallNumber) {
      alert("User or stall number not authenticated.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this listing and its associated media?")) {
      try {
        const listingDocRef = doc(db, `stalls/${userStallNumber}/listings`, listingIdToDelete);

        const allMediaUrls = [...(imageUrls || []), ...(videoUrls || [])];
        for (const url of allMediaUrls) {
          try {
            const fileRef = ref(storage, url);
            await deleteObject(fileRef);
          } catch (storageError: any) {
            console.warn(`Could not delete media ${url}: ${storageError.message}`);
          }
        }

        await deleteDoc(listingDocRef);
        alert("Listing and media deleted successfully!");
      } catch (error) {
        console.error("Error deleting document or media: ", error);
        alert("Failed to delete listing. Please try again.");
      }
    }
  };

  // Mark listing as sold
  const handleMarkAsSold = async (id: string) => {
    if (!userId || !userStallNumber) {
      alert("User or stall number not authenticated.");
      return;
    }
    try {
      const listingDocRef = doc(db, `stalls/${userStallNumber}/listings`, id);
      await updateDoc(listingDocRef, {
        sold: true,
        updatedAt: Timestamp.now(),
      });
      alert("Listing marked as sold!");
    } catch (error) {
      console.error("Error updating document: ", error);
      alert("Failed to mark listing as sold. Please try again.");
    }
  };

  // Navigation buttons
  const handleFinishPosting = () => {
    navigate('/');
  };
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const totalListings = listings.length;
  const soldListings = listings.filter((l: Listing) => l.sold).length;
  const availableListings = totalListings - soldListings;
  const totalRevenue = listings.reduce((sum, listing) => sum + (listing.sold ? (listing.price || 0) : 0), 0);

  // Render
  if (loadingFirebase || (!userStallNumber && !firebaseError)) {
    return <LoadingMessage>Initializing dashboard...</LoadingMessage>;
  }

  if (firebaseError) {
    return <ErrorMessage>Error: {firebaseError}</ErrorMessage>;
  }

  if (!userId) {
    return <ErrorMessage>Authentication required. Please ensure you are logged in.</ErrorMessage>;
  }

  if (!userStallNumber) {
    return (
      <ErrorMessage>
        Stall number not found. Please log in again from the stall login page.
      </ErrorMessage>
    );
  }

  return (
    <ListingsContainer>
      <DashboardHeader>
        <div>
          <DashboardTitle>
            <FontAwesomeIcon icon={faStore} /> Business Dashboard
          </DashboardTitle>
          <DashboardMeta>
            <span>
              Stall Number: <strong>{userStallNumber}</strong>
            </span>
            {stallDetails && (
              <>
                <span>
                  Business Name: <strong>{stallDetails.businessName}</strong>
                </span>
                <span>
                  Stall Type: <strong>{stallDetails.stallType}</strong>
                </span>
              </>
            )}
          </DashboardMeta>
        </div>
      </DashboardHeader>

      {/* 🔹 Payment Status */}
      {processingPayment && <LoadingMessage>{paymentStatusMessage}</LoadingMessage>}
      {!processingPayment && paymentStatusMessage && (
        <p style={{ textAlign: 'center', fontWeight: 'bold' }}>{paymentStatusMessage}</p>
      )}

      {/* 🔹 Display payment details if available */}
      {paymentDetails && (
        <div style={{ padding: '10px', background: '#f5f5f5', marginBottom: '10px' }}>
          <h4>Payment Details</h4>
          <pre>{JSON.stringify(paymentDetails, null, 2)}</pre>
        </div>
      )}

      <Tabs>
        <Tab active={activeTab === 'create'} onClick={() => setActiveTab('create')}>
          <TabIcon><FontAwesomeIcon icon={faPlus} /></TabIcon>
          <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>Post New Advert</span>
        </Tab>

        <Tab active={activeTab === 'my-listings'} onClick={() => setActiveTab('my-listings')}>
          <TabIcon><FontAwesomeIcon icon={faTags} /></TabIcon>
          <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>My Adverts</span>
        </Tab>

        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          <TabIcon><FontAwesomeIcon icon={faChartLine} /></TabIcon>
          <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>Overview</span>
        </Tab>

        <Tab active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
          <TabIcon><FontAwesomeIcon icon={faUser} /></TabIcon>
          <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>Profile</span>
        </Tab>

        <Button
          onClick={handleFinishPosting}
          style={{
            backgroundColor: '#1ed806ff',
            borderColor: '#6c757d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 15px',
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faHome} />
          <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>Finish Posting</span>
        </Button>

        <Button
          onClick={handleLogout}
          style={{
            backgroundColor: '#dc3545',
            borderColor: '#6c757d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 15px',
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faSignOutAlt} />
          <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>Logout</span>
        </Button>
      </Tabs>

      <DashboardContent>
        {activeTab === 'create' && (
          <PostAd
            userId={userId}
            userStallNumber={userStallNumber}
            stallDetails={stallDetails}
            onPostSuccess={() => setActiveTab('my-listings')}
          />
        )}
        {activeTab === 'my-listings' && (
          <MyListings
            listings={listings}
            loadingListings={loadingListings}
            listingsError={listingsError}
            handleDeleteListing={handleDeleteListing}
            handleMarkAsSold={handleMarkAsSold}
            setActiveTab={setActiveTab}
            stallNumber={userStallNumber}
          />
        )}
        {activeTab === 'overview' && (
          <Overview
            totalListings={totalListings}
            soldListings={soldListings}
            availableListings={availableListings}
            totalRevenue={totalRevenue}
            listings={listings}
            stallDetails={stallDetails}
          />
        )}
        {activeTab === 'profile' && (
          <Profile userStallNumber={userStallNumber} />
        )}
      </DashboardContent>
    </ListingsContainer>
  );
};

export default ListingsPage;
